export class FilterTree extends HTMLDivElement {
    private clickHandler: (e: MouseEvent) => void;
    private changeHandler: (e: Event) => void;

    constructor() {
        super();

        // Gère uniquement le toggle expand/collapse du bouton flèche
        this.clickHandler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const toggleBtn = target.closest<HTMLButtonElement>('.filter-tree-toggle');
            if (!toggleBtn) return;

            const item = toggleBtn.closest('.filter-tree-item')!;
            this.deplier(item, toggleBtn.getAttribute('aria-expanded') !== 'true');
        };

        // Gère les changements d'état des checkboxes (coche / décoche)
        this.changeHandler = (e: Event) => {
            const checkbox = e.target as HTMLInputElement;
            if (checkbox.type !== 'checkbox') return;

            const item = checkbox.closest('.filter-tree-item');
            if (!item) return;

            const parentRow = checkbox.closest('.filter-tree-row');
            const isParentRow = parentRow?.classList.contains('filter-tree-row--parent');

            if (isParentRow) {
                // Domaine parent coché : ses sous-domaines sont dépliés pour pouvoir affiner (CFB-519).
                // Décoché, la branche reste dans l'état choisi par l'utilisateur.
                if (checkbox.checked) this.deplier(item, true);

                // Domaine parent avec enfants : propager aux sous-domaines
                // Tous les enfants sont mis à jour avant d'émettre les change : chacun recalcule
                // l'état du parent, qui doit voir la fratrie complète.
                const children = Array.from(item.querySelectorAll<HTMLInputElement>('.filter-tree-children input[type="checkbox"]'))
                    .filter(child => child.checked !== checkbox.checked);
                children.forEach(child => child.checked = checkbox.checked);
                // Dispatch change pour que FormationSearch détecte la propagation
                children.forEach(child => child.dispatchEvent(new Event('change', { bubbles: true })));
            } else {
                // Sous-domaine ou domaine sans enfant : mettre à jour l'état du parent si applicable.
                // Un sous-domaine est rendu dans le .filter-tree-item de son parent : `item` est donc le parent.
                if (!checkbox.closest('.filter-tree-children')) return; // domaine sans enfant → change natif suffit

                const parentCheckbox = this.caseParent(item);
                if (!parentCheckbox) return;

                const { coches, total } = this.compterEnfants(item);
                parentCheckbox.checked = coches === total;
                parentCheckbox.indeterminate = coches > 0 && coches < total;
            }
        };
    }

    connectedCallback() {
        this.addEventListener('click', this.clickHandler);
        this.addEventListener('change', this.changeHandler);

        // L'état « partiellement coché » ne se rend pas en HTML : on le restaure au chargement
        // pour les domaines dont seule une partie des sous-domaines est active (CFB-519).
        // La case cochée reste celle rendue par le serveur, qui reflète l'URL.
        this.querySelectorAll('.filter-tree-item').forEach(item => {
            const parentCheckbox = this.caseParent(item);
            if (!parentCheckbox || parentCheckbox.checked) return;

            const { coches, total } = this.compterEnfants(item);
            parentCheckbox.indeterminate = coches > 0 && coches < total;
        });
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.clickHandler);
        this.removeEventListener('change', this.changeHandler);
    }

    /** Case du domaine parent d'un .filter-tree-item, null s'il n'a pas de sous-domaines. */
    private caseParent(item: Element): HTMLInputElement | null {
        return item.querySelector<HTMLInputElement>(':scope > .filter-tree-row--parent input[type="checkbox"]');
    }

    private compterEnfants(item: Element): { coches: number; total: number } {
        const enfants = Array.from(item.querySelectorAll<HTMLInputElement>('.filter-tree-children input[type="checkbox"]'));
        return { coches: enfants.filter(e => e.checked).length, total: enfants.length };
    }

    /** Déplie (ou replie) les sous-domaines d'un domaine parent. */
    private deplier(item: Element, ouvert: boolean): void {
        const toggleBtn = item.querySelector<HTMLButtonElement>(':scope > .filter-tree-row .filter-tree-toggle');
        const children = item.querySelector<HTMLElement>(':scope > .filter-tree-children');
        if (!toggleBtn || !children) return;

        toggleBtn.setAttribute('aria-expanded', String(ouvert));
        children.hidden = !ouvert;
    }
}
